import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, tenantStore } from '@/api/client';

/**
 * The customer's own colour and logo, set by the operator on the control plane.
 *
 * Read before sign-in — the sign-in screen is branded and there is no session
 * yet — and cached in localStorage per workspace, so a returning browser paints
 * the right brand on the first frame instead of flashing the product's own and
 * then repainting. The cache is only that first frame: the API is asked on
 * every start and again whenever the tab comes back into view, so a colour or
 * logo changed from the control plane reaches a tab left open all day. The
 * API answers `no-cache` with an ETag, so asking when nothing changed costs a
 * bodyless 304.
 */
export interface Branding {
  tenant: string | null;
  name: string | null;
  color: string | null;
  logo: string | null;
}

const EMPTY: Branding = { tenant: null, name: null, color: null, logo: null };
/** index.html's own title, kept for workspaces that have not set a logo. */
const PRODUCT_TITLE = 'Diwan';
const key = (tenant: string) => `erb.branding.${tenant}`;
/** Coming back to the tab asks again at most this often. */
const RECHECK_MS = 30_000;

function cached(tenant: string): Branding {
  try {
    const raw = localStorage.getItem(key(tenant));
    return raw ? { ...EMPTY, ...(JSON.parse(raw) as Branding) } : EMPTY;
  } catch {
    return EMPTY;
  }
}

/**
 * The brand, plus a way to replace it without a reload.
 *
 * The branding screen edits the very theme it is drawn in, so saving publishes
 * the new values here and every accent, the header mark and the nav repaint on
 * the spot. Without that the operator would have to reload to see what they
 * just did — and would not trust the preview.
 */
interface BrandingState extends Branding {
  apply: (next: Branding) => void;
}

const BrandingContext = createContext<BrandingState>({ ...EMPTY, apply: () => {} });

export const useBranding = (): BrandingState => useContext(BrandingContext);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const tenant = tenantStore.get();
  const [branding, setBranding] = useState<Branding>(() => cached(tenant));

  useEffect(() => {
    let cancelled = false;
    let lastAsked = 0;
    const load = () => {
      lastAsked = Date.now();
      api
        .get<Branding>('/branding')
        .then((fresh) => {
          if (cancelled) return;
          // Unchanged keeps the same object, so nothing repaints.
          setBranding((previous) => (JSON.stringify(previous) === JSON.stringify(fresh) ? previous : fresh));
          try {
            localStorage.setItem(key(tenant), JSON.stringify(fresh));
          } catch {
            // A quota failure costs a repaint on the next start, nothing more.
          }
        })
        .catch(() => {
          // Offline, or a workspace that no longer exists: the cached brand (or
          // the product's own) stands rather than blocking the sign-in screen.
        });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastAsked > RECHECK_MS) load();
    };
    load();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [tenant]);

  // The browser tab follows the header: a customer with their own logo sees
  // their own name there, not the product's.
  const tabTitle = branding.logo && branding.name ? branding.name : PRODUCT_TITLE;
  useEffect(() => {
    document.title = tabTitle;
  }, [tabTitle]);

  const value = useMemo<BrandingState>(
    () => ({
      ...branding,
      apply: (next) => {
        setBranding(next);
        try {
          localStorage.setItem(key(tenant), JSON.stringify(next));
        } catch {
          // As above: a quota failure costs a repaint on the next start.
        }
      },
    }),
    [branding, tenant],
  );
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}
